import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Select,
  Table,
  Modal,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  DatePicker
} from 'antd';
import {
  MailOutlined,
  MessageOutlined,
  SendOutlined,
  HistoryOutlined,
  BarChartOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { notificationServiceAPI, stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface NotificationHistory {
  _id: string;
  recipient: string;
  type: 'email' | 'sms';
  category: string;
  subject: string;
  message: string;
  status: 'pending' | 'sent' | 'failed' | 'delivered';
  sentAt?: string;
  error?: string;
  station?: {
    _id: string;
    name: string;
  };
}

const NotificationService: React.FC = () => {
  const [form] = Form.useForm();
  const [bulkForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<NotificationHistory[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await notificationServiceAPI.getNotificationTemplates();
      setTemplates(response.templates || []);
    } catch (error) {
      message.error('Şablonlar yüklenemedi');
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const params: any = {
        limit: 50,
        page: 1,
        ...filters
      };
      const response = await notificationServiceAPI.getNotificationHistory(params);
      setHistory(response.history || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Geçmiş yüklenemedi');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params: any = {};
      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      const response = await notificationServiceAPI.getNotificationStats(params);
      setStats(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İstatistikler yüklenemedi');
    }
  };

  useEffect(() => {
    fetchStations();
    fetchTemplates();
    fetchHistory();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);


  const handleSendNotification = async (values: any) => {
    setLoading(true);
    try {
      await notificationServiceAPI.sendNotification({
        recipient: values.recipient,
        type: values.type,
        category: values.category,
        subject: values.subject,
        message: values.message,
        relatedData: values.relatedData ? JSON.parse(values.relatedData) : {},
        stationId: values.stationId
      });
      message.success('Bildirim gönderildi');
      form.resetFields();
      fetchHistory();
      fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Bildirim gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSendBulk = async (values: any) => {
    setLoading(true);
    try {
      const recipients = values.recipients.split('\n').filter((r: string) => r.trim());
      const response = await notificationServiceAPI.sendBulkNotification({
        recipients,
        type: values.type,
        category: values.category,
        subject: values.subject,
        message: values.message,
        relatedData: values.relatedData ? JSON.parse(values.relatedData) : {},
        stationId: values.stationId
      });
      message.success(`${response.success} bildirim gönderildi, ${response.failed} başarısız`);
      bulkForm.resetFields();
      fetchHistory();
      fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Toplu bildirim gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (template: any) => {
    form.setFieldsValue({
      category: template.category,
      subject: template.subject,
      message: template.message
    });
    setModalVisible(false);
  };

  const handleDateRangeChange = (dates: any) => {
    setDateRange(dates);
    if (dates) {
      fetchStats();
    }
  };

  const historyColumns = [
    {
      title: 'Alıcı',
      dataIndex: 'recipient',
      key: 'recipient',
      render: (recipient: string, record: NotificationHistory) => (
        <Space>
          {record.type === 'email' ? <MailOutlined /> : <MessageOutlined />}
          {recipient}
        </Space>
      )
    },
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'email' ? 'blue' : 'green'}>
          {type === 'email' ? 'E-posta' : 'SMS'}
        </Tag>
      )
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Konu',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: any = {
          pending: 'default',
          sent: 'processing',
          failed: 'error',
          delivered: 'success'
        };
        const labels: any = {
          pending: 'Bekliyor',
          sent: 'Gönderildi',
          failed: 'Başarısız',
          delivered: 'Teslim Edildi'
        };
        return <Tag color={colors[status]}>{labels[status]}</Tag>;
      }
    },
    {
      title: 'Gönderim Zamanı',
      dataIndex: 'sentAt',
      key: 'sentAt',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-'
    }
  ];

  const tabItems = [
    {
      key: 'send',
      label: (
        <span>
          <SendOutlined />
          Bildirim Gönder
        </span>
      ),
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Tekil Bildirim Gönder">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSendNotification}
              >
                <Form.Item
                  name="type"
                  label="Bildirim Tipi"
                  rules={[{ required: true, message: 'Tip seçiniz' }]}
                >
                  <Select placeholder="Tip seçiniz">
                    <Option value="email">E-posta</Option>
                    <Option value="sms">SMS</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="recipient"
                  label="Alıcı"
                  rules={[{ required: true, message: 'Alıcı giriniz' }]}
                >
                  <Input placeholder="E-posta veya telefon numarası" />
                </Form.Item>
                <Form.Item
                  name="category"
                  label="Kategori"
                  rules={[{ required: true, message: 'Kategori seçiniz' }]}
                >
                  <Select placeholder="Kategori seçiniz">
                    <Option value="system">Sistem</Option>
                    <Option value="price_alert">Fiyat Uyarısı</Option>
                    <Option value="stock_alert">Stok Uyarısı</Option>
                    <Option value="campaign">Kampanya</Option>
                    <Option value="report">Rapor</Option>
                    <Option value="custom">Özel</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="stationId"
                  label="İstasyon (Opsiyonel)"
                >
                  <Select placeholder="İstasyon seçiniz" allowClear>
                    {stations.map(station => (
                      <Option key={station._id} value={station._id}>
                        {station.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="subject"
                  label="Konu"
                  rules={[{ required: true, message: 'Konu giriniz' }]}
                >
                  <Input placeholder="Bildirim konusu" />
                </Form.Item>
                <Form.Item
                  name="message"
                  label="Mesaj"
                  rules={[{ required: true, message: 'Mesaj giriniz' }]}
                >
                  <TextArea rows={4} placeholder="Bildirim mesajı" />
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />}>
                      Gönder
                    </Button>
                    <Button onClick={() => setModalVisible(true)} icon={<FileTextOutlined />}>
                      Şablon Kullan
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Toplu Bildirim Gönder">
              <Form
                form={bulkForm}
                layout="vertical"
                onFinish={handleSendBulk}
              >
                <Form.Item
                  name="type"
                  label="Bildirim Tipi"
                  rules={[{ required: true, message: 'Tip seçiniz' }]}
                >
                  <Select placeholder="Tip seçiniz">
                    <Option value="email">E-posta</Option>
                    <Option value="sms">SMS</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="recipients"
                  label="Alıcılar (Her satıra bir alıcı)"
                  rules={[{ required: true, message: 'Alıcıları giriniz' }]}
                >
                  <TextArea rows={6} placeholder="email1@example.com&#10;email2@example.com&#10;+905551234567" />
                </Form.Item>
                <Form.Item
                  name="category"
                  label="Kategori"
                  rules={[{ required: true, message: 'Kategori seçiniz' }]}
                >
                  <Select placeholder="Kategori seçiniz">
                    <Option value="system">Sistem</Option>
                    <Option value="price_alert">Fiyat Uyarısı</Option>
                    <Option value="stock_alert">Stok Uyarısı</Option>
                    <Option value="campaign">Kampanya</Option>
                    <Option value="report">Rapor</Option>
                    <Option value="custom">Özel</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="stationId"
                  label="İstasyon (Opsiyonel)"
                >
                  <Select placeholder="İstasyon seçiniz" allowClear>
                    {stations.map(station => (
                      <Option key={station._id} value={station._id}>
                        {station.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="subject"
                  label="Konu"
                  rules={[{ required: true, message: 'Konu giriniz' }]}
                >
                  <Input placeholder="Bildirim konusu" />
                </Form.Item>
                <Form.Item
                  name="message"
                  label="Mesaj"
                  rules={[{ required: true, message: 'Mesaj giriniz' }]}
                >
                  <TextArea rows={4} placeholder="Bildirim mesajı" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />} block>
                    Toplu Gönder
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      )
    },
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined />
          Geçmiş
        </span>
      ),
      children: (
        <Card
          title="Bildirim Geçmişi"
          extra={
            <Space>
              <Select
                placeholder="Tip"
                style={{ width: 120 }}
                allowClear
                onChange={(value) => setFilters({ ...filters, type: value })}
              >
                <Option value="email">E-posta</Option>
                <Option value="sms">SMS</Option>
              </Select>
              <Select
                placeholder="Durum"
                style={{ width: 120 }}
                allowClear
                onChange={(value) => setFilters({ ...filters, status: value })}
              >
                <Option value="pending">Bekliyor</Option>
                <Option value="sent">Gönderildi</Option>
                <Option value="failed">Başarısız</Option>
                <Option value="delivered">Teslim Edildi</Option>
              </Select>
            </Space>
          }
        >
          <Table
            columns={historyColumns}
            dataSource={history}
            loading={historyLoading}
            rowKey="_id"
            pagination={{
              pageSize: 20,
              showSizeChanger: false,
              responsive: true
            }}
          />
        </Card>
      )
    },
    {
      key: 'stats',
      label: (
        <span>
          <BarChartOutlined />
          İstatistikler
        </span>
      ),
      children: (
        <Card
          title="Bildirim İstatistikleri"
          extra={
            <RangePicker
              onChange={handleDateRangeChange}
              format="DD.MM.YYYY"
            />
          }
        >
          {stats && (
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Toplam Gönderilen"
                  value={stats.totalSent || 0}
                  prefix={<SendOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Başarısız"
                  value={stats.totalFailed || 0}
                  prefix={<MessageOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Başarı Oranı"
                  value={stats.successRate || 0}
                  suffix="%"
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
            </Row>
          )}
        </Card>
      )
    }
  ];

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Bildirim Servisi
      </Title>

      <Tabs items={tabItems} />

      <Modal
        title="Şablon Seç"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {templates.map(template => (
            <Card
              key={template.id}
              size="small"
              hoverable
              onClick={() => handleUseTemplate(template)}
              style={{ cursor: 'pointer' }}
            >
              <Text strong>{template.name}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {template.subject}
              </Text>
            </Card>
          ))}
        </Space>
      </Modal>
    </div>
  );
};

export default NotificationService;

